import { AxCounter } from "../../../Limestone/AxCounter/AxCounter";

/**
 * Cross-sale options of a product selection, set by the ticket views and the rental details view when the page comes
 * from a valid cross-sale offer of the shopping cart and the single selection config of the product type is on
 * (C#: CrossSaleTicketSelectionHelper, ConfigDefinition.CrossSaleTicketSingleSelection / CrossSaleRentalSingleSelection).
 */
export interface ICrossSaleSelectionOptions {
    /** Maximum total quantity of all person types together (1: only the cross-sale ticket). */
    CrossSaleMaxQuantity?: number;
    /** Cross-sale discount in percent, applied to the displayed total only (not to unit, button or calendar prices). */
    CrossSaleDiscountPercent?: number;
    /**
     * The page comes from a valid cross-sale offer (whatever the single selection config is). The ticket selection then
     * ignores the best match: the cart never transforms a cross-sale ticket into a package.
     */
    IsCrossSale?: boolean;
}

export class CrossSaleSelectionHelper {
    /**
     * When the sum of all counters reaches <paramref name="maxTotal"/>, every counter gets its current value as maximum,
     * which disables all "+" buttons (AxCounter.updateButtonState). Below it, the original maximums are restored.
     * Uses updateMax because AxCounter removes a manually set "disabled" on every change.
     */
    static limitTotalQuantity(counters: AxCounter[], maxes: number[], maxTotal?: number): void {
        if (!maxTotal || !counters?.length) {
            return;
        }

        const total = counters.reduce((sum, counter) => sum + (counter.GetValue() ?? 0), 0);

        counters.forEach((counter, index) => counter.updateMax(total >= maxTotal ? counter.GetValue() : maxes[index]));
    }

    /**
     * Cross-sale discount (cents) of one ticket. Only the ticket itself is discounted: its mandatory articles are separate
     * shopping cart positions without discount. The discounted price is truncated like the shopping cart does.
     * Same calculation as CrossSaleTicketSelectionHelper.GetTicketDiscount (C#, Summary).
     * @param ticketPrice price of one ticket including its mandatory articles (Product.Price or the best match tariff)
     */
    static getTicketDiscount(ticketPrice: number, mandatoryArticlesPrice?: number, discountPercent?: number): number {
        return CrossSaleSelectionHelper.getDiscount(Math.max(0, ticketPrice - (mandatoryArticlesPrice ?? 0)), discountPercent);
    }

    /**
     * Cross-sale discount (cents) of the first participant of a lesson, from the price that participant already pays.
     * The shopping cart keeps only the bigger one of its position discount (private lessons) and its cross-sale discount,
     * so there is no discount when the position price is already lower.
     * Same calculation as CrossSaleTicketSelectionHelper.GetLessonDiscount (C#, lessons V2 personalization).
     * @param firstParticipantPrice price of the first participant (position price, cents)
     * @param tariff tariff of the lesson without position discount (cents), the base of the cross-sale discount
     */
    static getLessonDiscount(firstParticipantPrice: number, tariff: number, discountPercent?: number): number {
        const crossSalePrice = tariff - CrossSaleSelectionHelper.getDiscount(tariff, discountPercent);

        return Math.max(0, firstParticipantPrice - crossSalePrice);
    }

    /**
     * Cross-sale discount (cents) of one product of <paramref name="price"/> (cents), with the discounted price truncated
     * like the shopping cart does (DiscountHelper.ApplyCrossSaleDiscounts).
     */
    static getDiscount(price: number, discountPercent?: number): number {
        if (!discountPercent || !price) {
            return 0;
        }

        // the epsilon avoids floating point results like 899.9999 being truncated to 899
        return price - Math.floor(price * (100 - discountPercent) / 100 + 1e-6);
    }
}
